import { useContext, useRef, useState } from 'react';
import ModelsContext from '../ModelsContext';
import ModelSettings from './ModelSettings';
import type ModelType from '@/lib/constants/challengeModel/ModelType.enum';
import CreateModel from './CreateModel';
import { Divider, MenuItem, Select } from '@mui/material';
import SelectorWrapper from './styled-components/SelectorWrapper';
import DeactivatedModelIndicator from './styled-components/DeactivatedModelIndicator';
import DefaultModelIndicator from './styled-components/DefaultModelIndicator';
import { FormattedMessage, useIntl } from 'react-intl';

type ModelSelectorProps = {
  selectedModelType: ModelType;
  selectedModelId: number;
  setSelectedModelId: (model: number) => void;
};

const ModelSelector = ({
  selectedModelType,
  selectedModelId,
  setSelectedModelId,
}: ModelSelectorProps) => {
  const [models] = useContext(ModelsContext);

  const [selectOpen, setSelectOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const openDialogAfterSelectClose = useRef(false);

  const openCreateDialog = (): void => {
    openDialogAfterSelectClose.current = true;
    setSelectOpen(false);
  };

  const closeCreateDialog = (): void => {
    setCreateDialogOpen(false);
    setSelectOpen(true);
  };

  const intl = useIntl();
  const modelNameFallback = intl.formatMessage({
    id: 'editor.model_editor.model_selector.original_model_name_fallback',
  });

  return (
    <SelectorWrapper>
      <label id="model-select-label" htmlFor="model-select">
        <FormattedMessage id="editor.model_editor.model_selector.label" />
      </label>
      <Select
        id="model-select"
        labelId="model-select-label"
        variant="standard"
        size="small"
        value={selectedModelId}
        onChange={e => setSelectedModelId(e.target.value)}
        renderValue={modelId => models[modelId].name ?? modelNameFallback}
        open={selectOpen}
        onOpen={() => setSelectOpen(true)}
        onClose={() => setSelectOpen(false)}
        MenuProps={{
          keepMounted: true,
          slotProps: {
            transition: {
              onExited: (): void => {
                if (openDialogAfterSelectClose.current) {
                  setCreateDialogOpen(true);

                  openDialogAfterSelectClose.current = false;
                }
              },
            },
          },
        }}
      >
        {Object.values(models)
          .filter(model => model.type === selectedModelType)
          .map(model => (
            <MenuItem value={model.id} key={model.id}>
              {model.name ?? modelNameFallback}
              {model.isDefault && (
                <DefaultModelIndicator>
                  <FormattedMessage id="editor.model_editor.model_selector.select.model_option.default_indicator" />
                </DefaultModelIndicator>
              )}
              {model.deactivatedAt !== null && (
                <DeactivatedModelIndicator>
                  <FormattedMessage id="editor.model_editor.model_selector.select.model_option.deactivated_indicator" />
                </DeactivatedModelIndicator>
              )}
            </MenuItem>
          ))}
        <Divider />
        <CreateModel
          selectedModelType={selectedModelType}
          dialogOpen={createDialogOpen}
          onOpenDialog={openCreateDialog}
          onCloseDialog={closeCreateDialog}
        />
      </Select>
      <ModelSettings model={models[selectedModelId]} />
    </SelectorWrapper>
  );
};

export default ModelSelector;
